import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'

interface PrefetchOptions {
  delay?: number // Delay before prefetching (default: 200ms)
  priority?: boolean // High priority prefetch
}

/**
 * Hook for intelligent route prefetching
 * Prefetches routes on hover, visibility, or programmatically
 */
export function usePrefetch() {
  const router = useRouter()
  const prefetchedRoutes = useRef<Set<string>>(new Set())
  const hoverTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Prefetch a route
  const prefetch = (href: string, options: PrefetchOptions = {}) => {
    // Skip if already prefetched
    if (prefetchedRoutes.current.has(href)) {
      return
    }

    // Skip external links
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
      return
    }

    // Skip if it's the current route
    if (typeof window !== 'undefined' && window.location.pathname === href) {
      return
    }

    try {
      router.prefetch(href)
      prefetchedRoutes.current.add(href)
    } catch (error) {
      console.warn('Failed to prefetch route:', href, error)
    }
  }

  // Prefetch on hover with delay
  const prefetchOnHover = (href: string, options: PrefetchOptions = {}) => {
    const delay = options.delay ?? 200

    // Clear existing timeout if any
    const existingTimeout = hoverTimeouts.current.get(href)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }

    // Set new timeout
    const timeout = setTimeout(() => {
      prefetch(href, options)
      hoverTimeouts.current.delete(href)
    }, delay)

    hoverTimeouts.current.set(href, timeout)
  }

  // Cancel hover prefetch
  const cancelHoverPrefetch = (href: string) => {
    const timeout = hoverTimeouts.current.get(href)
    if (timeout) {
      clearTimeout(timeout)
      hoverTimeouts.current.delete(href)
    }
  }

  // Prefetch on viewport visibility (Intersection Observer)
  // This returns a hook that should be used in a component
  const usePrefetchOnVisible = (
    elementRef: React.RefObject<HTMLElement>,
    href: string,
    options: PrefetchOptions = {}
  ) => {
    useEffect(() => {
      if (!elementRef.current) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              prefetch(href, options)
              observer.disconnect()
            }
          })
        },
        {
          rootMargin: '200px', // Start prefetching 200px before element is visible
        }
      )

      observer.observe(elementRef.current)

      return () => {
        observer.disconnect()
      }
    }, [elementRef, href])
  }

  // Prefetch likely next routes
  const prefetchLikelyRoutes = (currentPath: string) => {
    const routeMap: Record<string, string[]> = {
      '/signup': ['/campaigns', '/verify'],
      '/login': ['/campaigns', '/verify'],
      '/verify': ['/campaigns'],
      '/campaigns': [],
    }

    const routes = routeMap[currentPath] || []
    routes.forEach((route) => {
      prefetch(route, { delay: 0 })
    })
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts
      hoverTimeouts.current.forEach((timeout) => clearTimeout(timeout))
      hoverTimeouts.current.clear()
    }
  }, [])

  return {
    prefetch,
    prefetchOnHover,
    cancelHoverPrefetch,
    usePrefetchOnVisible,
    prefetchLikelyRoutes,
  }
}

